/**
 * Dialogue writing formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_writing_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const dialogueWritingFormatsPack: ToilePack = {
    slug: 'dialogue-writing-formats',
    category: 'classroom',
    name: 'Dialogue writing formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'friends', label: 'Two friends', subject: 'a page with speaker names on the left and lines on the right, labelled "Speaker" and "Dialogue"'},
        {key: 'shop', label: 'At the shop', subject: 'a dialogue format with two speech-bubble columns'},
        {key: 'teacher', label: 'Teacher and student', subject: 'a dialogue format with alternating names and ruled lines'},
    ],
    styles: [
        {key: 'notebook', label: 'Notebook page', style: 'school exercise notebook page, neat hand-lettered pen labels on ruled lines, red margin line', palette: 'blue ruled lines, black pen labels, red margin line', framing: 'flat top-down page filling the frame, straight on'},
        {key: 'colorcoded', label: 'Colour-coded', style: 'clean printable teaching worksheet, bold rounded headings in boxes, each part of the format in its own colour', palette: 'navy, teal and orange labels and boxes on white paper', framing: 'flat page centered with an even white margin'},
        {key: 'mono', label: 'Black and white', style: 'photocopy-ready black-and-white worksheet, thin crisp outlines, bracket and arrow callouts naming each part', palette: 'black ink only', framing: 'flat page centered with an even white margin'},
    ],
    directions: ['red pen marks', 'filled sample', 'bigger labels'],
    directionHint: 'try: red pen marks',
    seo: {
        title: 'Dialogue Writing Format: Two Friends, Shop, Teacher',
        metaDescription: 'Dialogue writing format on one labelled page: a dialogue between two friends, at a shop and between a teacher and student, in three styles.',
        h1: 'Dialogue writing format: two friends, a shop, teacher and student',
        tagline: 'Nine labelled pages, three dialogue layouts in notebook, colour-coded and black and white styles.',
        intro: 'A printable worksheet showing how a dialogue is set out, with speaker names on the left and lines of speech on the right. The nine pages show a dialogue between two friends, a customer and a shopkeeper, and a teacher and a student. The shop page uses two speech-bubble columns. Ask for another dialogue and the same labelled layout is asked for.',
        useCases: [
            'a classroom lesson on dialogue writing',
            'a reference for a role-play task',
            'a handout for a language class',
            'a sheet for practising dialogue',
        ],
        faq: [
            {q: 'How is a dialogue laid out?', a: 'Speaker names sit on the left, and the lines of speech sit on the right.'},
            {q: 'Does the page show a finished dialogue?', a: 'No. The lines are ruled, so the page shows the layout, not the words.'},
            {q: 'How do the three styles differ?', a: 'Notebook page is hand-lettered pen labels on ruled lines with a red margin, like a school exercise book. Colour-coded gives each part its own colour in a box. Black and white is thin outlines in ink only, ready to photocopy.'},
            {q: 'Can I ask for a dialogue in another setting?', a: 'Yes. Add a direction for another setting and the labelled layout is asked for with it.'},
        ],
    },
}
